import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Eye, FileText, Trash2, Upload } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';
import { cn } from '../../../lib/utils';
import { formatShortDate, toDateInputValue } from '../../../utils/dateUtils';
import type { PolicyListItem } from '../../../services/api/policyService';
import { savePolicyDocument } from '../../../services/api/policyService';
import { convertDocxToHtml } from '../../../lib/docx';
import PolicyEditDialog from './PolicyEditDialog';

export interface ParsedPolicy {
    _id?: string;
    fileName?: string;
    title: string;
    effectiveDate: string;
    expiryDate: string;
    tags: string[];
    html: string;
    warnings: string[];
    parsedAt: number;
}

const toShortDate = (iso?: string): string =>
    iso ? toDateInputValue(new Date(iso)) : '';

export const policyListItemToParsed = (item: PolicyListItem): ParsedPolicy => ({
    _id: item._id,
    title: item.title ?? 'Untitled policy',
    effectiveDate: toShortDate(item.effectiveDate),
    expiryDate: toShortDate(item.expiryDate),
    tags: item.tags ?? [],
    html: '',
    warnings: [],
    parsedAt: new Date(item.createdAt).getTime(),
});

const policyKey = (policy: ParsedPolicy): string =>
    policy._id ?? policy.fileName ?? policy.title;

export const stripDocxExtension = (fileName: string): string =>
    fileName.replace(/\.docx$/i, '');

interface DocxPolicyUploadProps {
    policies: ParsedPolicy[];
    onAdd: (policy: ParsedPolicy) => Promise<void>;
    onRemove: (policy: ParsedPolicy) => Promise<void>;
    onUpdate?: (updated: ParsedPolicy) => void;
}

export default function DocxPolicyUpload({
    policies,
    onAdd,
    onRemove,
    onUpdate,
}: DocxPolicyUploadProps) {
    const { toast } = useToast();
    const { t } = useTranslation();
    const [parsing, setParsing] = useState(false);
    const [dialogPolicy, setDialogPolicy] = useState<ParsedPolicy | null>(
        null
    );

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;

            if (policies.some((p) => p.fileName && p.fileName === file.name)) {
                toast({
                    title: t('facilities.policyUpload.alreadyUploaded'),
                    description: t('facilities.policyUpload.alreadyUploadedDesc', { fileName: file.name }),
                    variant: 'destructive',
                });
                return;
            }

            setParsing(true);
            try {
                const arrayBuffer = await file.arrayBuffer();
                const { html, warnings } = await convertDocxToHtml(arrayBuffer);
                setDialogPolicy({
                    fileName: file.name,
                    title: stripDocxExtension(file.name),
                    effectiveDate: '',
                    expiryDate: '',
                    tags: [],
                    html,
                    warnings,
                    parsedAt: Date.now(),
                });
            } catch (err) {
                toast({
                    title: t('facilities.policyUpload.uploadFailed'),
                    description:
                        err instanceof Error
                            ? err.message
                            : t('facilities.policyUpload.uploadFailedDesc', { fileName: file.name }),
                    variant: 'destructive',
                });
            } finally {
                setParsing(false);
            }
        },
        [policies, toast, t]
    );

    const openPreview = (policy: ParsedPolicy) => {
        setDialogPolicy(policy);
    };

    const handleDialogSave = async (policy: ParsedPolicy): Promise<void> => {
        if (policy._id) {
            await savePolicyDocument(
                policy._id,
                { policyData: policy.html },
                policy.title,
                'Edited from onboarding',
                {
                    effectiveDate: new Date(
                        policy.effectiveDate
                    ).toISOString(),
                    expiryDate: new Date(policy.expiryDate).toISOString(),
                    tags: policy.tags,
                }
            );
            onUpdate?.(policy);
        } else {
            await onAdd(policy);
        }
    };

    const handleDialogClose = (open: boolean) => {
        if (!open) setDialogPolicy(null);
    };

    const handleRemove = async (policy: ParsedPolicy) => {
        try {
            await onRemove(policy);
        } catch (err) {
            toast({
                title: t('facilities.policyUpload.removeFailed'),
                description:
                    err instanceof Error
                        ? err.message
                        : t('facilities.policyUpload.removeFailedDesc', { title: policy.title }),
                variant: 'destructive',
            });
        }
    };

    return (
        <div className="space-y-4">
            <label
                className={cn(
                    'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-subtle bg-muted/30 p-8 transition-colors',
                    'hover:border-emerald-500/40 hover:bg-muted/50',
                    parsing && 'pointer-events-none opacity-60'
                )}
            >
                <Upload
                    className="h-8 w-8 text-muted-foreground"
                    aria-hidden="true"
                />
                <div className="text-center">
                    <span className="text-sm font-medium text-foreground">
                        {parsing
                            ? t('facilities.policyUpload.parsing')
                            : t('facilities.policyUpload.clickToUpload')}
                    </span>
                    <p className="mt-1 text-xs text-muted-foreground">
                        {t('facilities.policyUpload.uploadHint')}
                    </p>
                </div>
                <input
                    type="file"
                    accept=".docx"
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={parsing}
                    aria-label="Upload policy document"
                />
            </label>

            {policies.length > 0 && (
                <div className="space-y-2">
                    {policies.map((policy) => {
                        const hasWarnings = policy.warnings.length > 0;
                        const key = policyKey(policy);
                        return (
                            <div
                                key={key}
                                className="flex items-center justify-between gap-4 rounded-lg border border-subtle bg-muted px-4 py-2.5"
                            >
                                <div className="flex min-w-0 items-start gap-3">
                                    <FileText
                                        className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                                        aria-hidden="true"
                                    />
                                    <div className="min-w-0 space-y-1">
                                        <p className="truncate text-sm font-medium text-foreground">
                                            {policy.title}
                                        </p>
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <span className="text-xs text-muted-foreground">
                                                {t('facilities.policyUpload.effective', { date: formatShortDate(policy.effectiveDate) })}
                                            </span>
                                            {policy.tags.map((tag) => (
                                                <Badge
                                                    key={tag}
                                                    variant="secondary"
                                                    className="h-5 border-0 bg-zinc-200 px-1.5 text-[11px] font-normal text-zinc-800"
                                                >
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </div>
                                        {hasWarnings && (
                                            <p className="flex items-center gap-1.5 text-xs text-amber-400">
                                                <AlertTriangle
                                                    className="h-3 w-3"
                                                    aria-hidden="true"
                                                />
                                                {t('facilities.policyUpload.formattingWarning', { count: policy.warnings.length })}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openPreview(policy)}
                                        aria-label={`Preview and edit ${policy.title}`}
                                    >
                                        <Eye
                                            className="h-4 w-4"
                                            aria-hidden="true"
                                        />
                                        <span className="ml-1.5 hidden sm:inline">
                                            {t('facilities.policyUpload.previewAndEdit')}
                                        </span>
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemove(policy)}
                                        aria-label={`Remove ${policy.title}`}
                                        className="text-muted-foreground hover:text-destructive"
                                    >
                                        <Trash2
                                            className="h-4 w-4"
                                            aria-hidden="true"
                                        />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <PolicyEditDialog
                open={dialogPolicy !== null}
                onOpenChange={handleDialogClose}
                policy={dialogPolicy}
                onSave={handleDialogSave}
            />
        </div>
    );
}
